/**
 * WooCommerce notice auto-dismiss, close buttons, and AJAX re-init.
 *
 * Handles:
 * - Auto-dismiss for success notices (IntersectionObserver + pause-on-hover)
 * - Close button click handler for all notice types
 * - MutationObserver to re-initialize AJAX-loaded notices (checkout errors)
 * - Respects prefers-reduced-motion
 *
 * @package Sansegynge
 */

const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/** Tracks notices that have already been initialized to avoid duplicates. */
const initializedNotices = new WeakSet();

/**
 * Dismiss a notice with slide-out animation (or immediately for reduced motion).
 *
 * @param {HTMLElement} el - The notice element to dismiss.
 */
function dismissNotice(el) {
	if (reducedMotion) {
		el.remove();
		return;
	}

	el.classList.add('sg-notice--dismissing');
	el.addEventListener('animationend', () => el.remove(), { once: true });
}

/**
 * Set up auto-dismiss timer with IntersectionObserver and pause-on-hover.
 *
 * @param {HTMLElement} notice - The notice element with data-auto-dismiss.
 */
function setupAutoDismiss(notice) {
	const dismissTime = parseInt(notice.dataset.autoDismiss, 10);
	if (!dismissTime || dismissTime <= 0) {
		return;
	}

	let remaining = dismissTime;
	let timerId = null;
	let startedAt = null;

	function startTimer() {
		startedAt = Date.now();
		timerId = setTimeout(() => dismissNotice(notice), remaining);
	}

	function pauseTimer() {
		if (timerId) {
			clearTimeout(timerId);
			timerId = null;
			remaining -= Date.now() - startedAt;
			if (remaining < 0) {
				remaining = 0;
			}
		}
	}

	// Pause on hover, restart on leave.
	notice.addEventListener('mouseenter', pauseTimer);
	notice.addEventListener('mouseleave', () => {
		if (remaining > 0) {
			startTimer();
		}
	});

	// Only start the timer when the notice is visible in the viewport.
	const observer = new IntersectionObserver(
		(entries) => {
			entries.forEach((entry) => {
				if (entry.isIntersecting) {
					startTimer();
					observer.unobserve(notice);
				}
			});
		},
		{ threshold: 0.1 }
	);

	observer.observe(notice);
}

/**
 * Initialize notice behavior on all [data-sg-notice] elements.
 *
 * Safe to call multiple times -- already-initialized notices are skipped.
 */
export function initNotices() {
	document.querySelectorAll('[data-sg-notice]').forEach((notice) => {
		if (initializedNotices.has(notice)) {
			return;
		}
		initializedNotices.add(notice);

		// Auto-dismiss (only success notices have data-auto-dismiss).
		if (notice.hasAttribute('data-auto-dismiss')) {
			setupAutoDismiss(notice);
		}

		// Close button handler.
		const closeBtn = notice.querySelector('[data-sg-notice-close]');
		if (closeBtn) {
			closeBtn.addEventListener('click', () => dismissNotice(notice));
		}
	});
}

/**
 * Observe DOM for dynamically added notices (AJAX checkout errors, etc.).
 *
 * Uses MutationObserver on document.body to detect new [data-sg-notice]
 * elements and re-run initNotices(). Also listens for WC jQuery events
 * if jQuery is available.
 */
export function observeNewNotices() {
	// MutationObserver for any new notices added to the DOM.
	const observer = new MutationObserver((mutations) => {
		let hasNewNotices = false;

		for (const mutation of mutations) {
			for (const node of mutation.addedNodes) {
				if (node.nodeType !== Node.ELEMENT_NODE) {
					continue;
				}

				// Check if the added node is a notice or contains notices.
				if (
					node.hasAttribute?.('data-sg-notice') ||
					node.querySelector?.('[data-sg-notice]')
				) {
					hasNewNotices = true;
					break;
				}
			}

			if (hasNewNotices) {
				break;
			}
		}

		if (hasNewNotices) {
			initNotices();
		}
	});

	observer.observe(document.body, {
		childList: true,
		subtree: true,
	});

	// Also listen for WC jQuery events if jQuery is available.
	if (typeof jQuery !== 'undefined') {
		jQuery(document.body).on('checkout_error', () => initNotices());
		jQuery(document.body).on('wc_fragments_refreshed', () => initNotices());
	}
}
